import React, { useRef, useEffect } from 'react';
import {
  Search, X, Plus, FileText, GitBranch, Home, Target,
  TrendingUp, Network, Brain, Lightbulb,
  ShieldCheck, Key, BarChart3, Activity, Eye
} from 'lucide-react';
import { theme } from '../../styles/theme';
import config from '../../config';

interface CommandPaletteProps {
  isOpen: boolean;
  onClose: () => void;
  darkMode: boolean;
  onSectionChange: (section: string) => void;
}

const CommandPalette: React.FC<CommandPaletteProps> = ({
  isOpen,
  onClose,
  darkMode,
  onSectionChange,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const commandItems = [
    { category: 'Quick Actions', items: [
      { icon: Search, label: 'Search Skills', shortcut: '⌘F', action: () => { onSectionChange('skill-search'); onClose(); } },
      { icon: FileText, label: 'Generate Report', shortcut: '⌘R', action: () => {} },
      { icon: Plus, label: 'New Cluster', shortcut: '⌘N', action: () => {} },
      { icon: Eye, label: 'View Insights', shortcut: '⌘I', action: () => { onSectionChange('insights-overview'); onClose(); } },
    ]},
    { category: 'Services', items: [
      { icon: Network, label: 'Nexus → Status', shortcut: '', action: () => onSectionChange('service-nexus') },
      { icon: GitBranch, label: 'Bridge → Connections', shortcut: '', action: () => onSectionChange('service-bridge') },
      { icon: Brain, label: 'Symphony → Query', shortcut: '', action: () => onSectionChange('service-symphony') },
      { icon: Lightbulb, label: 'Hypothesis Engine → Test', shortcut: '', action: () => onSectionChange('service-hypothesis') },
    ]},
    { category: 'Navigation', items: [
      { icon: Home, label: 'Go to Home', shortcut: '⌘1', action: () => { onSectionChange('home'); onClose(); } },
      { icon: Target, label: 'Skill & Motive Match', shortcut: '⌘2', action: () => { onSectionChange('skill-motive'); onClose(); } },
      { icon: TrendingUp, label: 'Go to Insights', shortcut: '⌘3', action: () => { onSectionChange('insights'); onClose(); } },
      { icon: ShieldCheck, label: 'Administration', shortcut: '⌘A', action: () => { onSectionChange('administration'); onClose(); } },
    ]},
    { category: 'System Admin', items: [
      ...(config.keycloakAdminUrl ? [{ icon: Key, label: 'Open Keycloak', shortcut: '', action: () => window.open(config.keycloakAdminUrl!, '_blank') }] : []),
      ...(config.grafanaUrl ? [{ icon: BarChart3, label: 'Open Grafana', shortcut: '', action: () => window.open(config.grafanaUrl!, '_blank') }] : []),
      ...(config.jaegerUrl ? [{ icon: Activity, label: 'Open Jaeger', shortcut: '', action: () => window.open(config.jaegerUrl!, '_blank') }] : []),
    ]},
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center pt-32 z-50">
      <div className="w-full max-w-2xl rounded-xl overflow-hidden" style={{
        backgroundColor: darkMode ? theme.colors.background.darkPaper : theme.colors.background.paper,
        boxShadow: '0 20px 60px rgba(228, 165, 128, 0.3)',
      }}>
        <div className="p-4" style={{
          borderBottom: `1px solid ${darkMode ? theme.colors.border.dark : theme.colors.border.light}`,
        }}>
          <div className="flex items-center space-x-3">
            <Search className="w-5 h-5" style={{ color: theme.colors.text.secondary }} />
            <input
              ref={inputRef}
              type="text"
              placeholder="Type a command or search..."
              className="flex-1 text-lg focus:outline-none bg-transparent"
              style={{
                color: darkMode ? theme.colors.text.darkPrimary : theme.colors.text.primary,
              }}
            />
            <button
              onClick={onClose}
              className="p-1 rounded hover:bg-black hover:bg-opacity-10"
            >
              <X className="w-5 h-5" style={{ color: theme.colors.text.secondary }} />
            </button>
          </div>
        </div>
        <div className="max-h-96 overflow-y-auto">
          {commandItems.map((category, categoryIndex) => (
            <div key={categoryIndex}>
              <div className="px-4 py-2 text-xs font-semibold uppercase tracking-wider" style={{
                backgroundColor: darkMode ? theme.colors.background.dark : theme.colors.background.default,
                color: theme.colors.text.secondary,
              }}>
                {category.category}
              </div>
              {category.items.map((item, itemIndex) => {
                const Icon = item.icon;
                return (
                  <button
                    key={itemIndex}
                    onClick={() => {
                      item.action();
                      if (category.category === 'Navigation') {
                        onClose();
                      }
                    }}
                    className="w-full px-4 py-3 flex items-center justify-between transition-colors hover:bg-black hover:bg-opacity-5"
                    style={{
                      color: darkMode ? theme.colors.text.darkSecondary : theme.colors.text.primary,
                    }}
                  >
                    <div className="flex items-center space-x-3">
                      <Icon className="w-5 h-5" style={{ color: theme.colors.text.secondary }} />
                      <span>{item.label}</span>
                    </div>
                    {item.shortcut && (
                      <span className="text-xs" style={{ color: theme.colors.text.secondary }}>
                        {item.shortcut}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CommandPalette;